import { useCallback, useState } from 'react';
import { BRAND } from '@app/shared';
import { useAppApi } from '../../app/ApiProvider';
import { useAsync } from '../../hooks/useAsync';
import { copyText } from '../../telegram/telegram';
import { SmallButton } from '../../ui/Cta';
import { ErrorState, Loader } from '../../ui/feedback';
import { Screen, ScrollArea, SectionTitle, TopBar } from '../../ui/Screen';
import type { AdminBusinessRowDto, AdminFunnelRowDto, AdminTotalsDto } from '../../api/admin';

/**
 * ponytail: весь текст экрана — строками, а не ключами i18n. Это внутренний экран владельца
 * продукта (доступ закрыт `ADMIN_TG_ID` на бэкенде), он один и русскоязычный; узбекской
 * версии у приватной статистики нет. Бизнесам и клиентам экран не виден — заводить словарь
 * RU+UZ незачем. Числа и воронку считает бэкенд (`adminService`), здесь только показ.
 */
export function AdminScreen(props: {
  onBack: () => void;
  onOpenBusiness: (businessId: string) => void;
}): JSX.Element {
  const api = useAppApi();
  const { state, reload } = useAsync(
    useCallback(() => api.admin.overview(), [api]),
    [],
  );

  return (
    <Screen>
      <TopBar title="Статистика платформы" onBack={props.onBack} />
      <ScrollArea bottomGap="none">
        {state.status === 'loading' ? <Loader /> : null}
        {state.status === 'failed' ? <ErrorState error={state.error} onRetry={reload} /> : null}

        {state.status === 'ready' ? (
          <>
            <div className="px-4 pt-4">
              <SectionTitle>Итоги</SectionTitle>
              <Totals totals={state.data.totals} />
            </div>

            <div className="px-4 pt-5">
              <SectionTitle>Ссылки-метки</SectionTitle>
              <LinkBuilder funnel={state.data.funnel} />
            </div>

            <div className="px-4 pt-5">
              <SectionTitle>Откуда приходят</SectionTitle>
              <Funnel rows={state.data.funnel} />
            </div>

            <div className="px-4 pt-5">
              <SectionTitle>Организации · {state.data.businesses.length}</SectionTitle>
              <Businesses rows={state.data.businesses} onOpen={props.onOpenBusiness} />
            </div>
          </>
        ) : null}
      </ScrollArea>
    </Screen>
  );
}

function Cell(props: { label: string; value: number }): JSX.Element {
  return (
    <div className="rounded-card bg-surface px-3 py-3 shadow-[inset_0_0_0_1px_var(--hair)]">
      <div className="text-[22px] font-extrabold tracking-[-0.02em]">{props.value}</div>
      <div className="mt-0.5 text-[11.5px] text-ink-3">{props.label}</div>
    </div>
  );
}

function Totals(props: { totals: AdminTotalsDto }): JSX.Element {
  const t = props.totals;
  return (
    <div className="grid grid-cols-3 gap-2">
      <Cell label="Клиентов" value={t.clients} />
      <Cell label="Организаций" value={t.businesses} />
      <Cell label="В каталоге" value={t.published} />
      <Cell label="Записей" value={t.appointments} />
      <Cell label="Завершено" value={t.completed} />
      <Cell label="Неявок" value={t.noShow} />
      <Cell label="Pro" value={t.pro} />
      <Cell label="Триал" value={t.trial} />
      <Cell label="Free" value={t.free} />
    </div>
  );
}

function Funnel(props: { rows: readonly AdminFunnelRowDto[] }): JSX.Element {
  if (props.rows.length === 0) {
    return (
      <div className="rounded-card bg-surface px-3 py-3 text-[13px] text-ink-3 shadow-[inset_0_0_0_1px_var(--hair)]">
        Меток источника пока нет. Ссылки с меткой: <span className="font-mono">t.me/KanvelBot?start=src_…</span>
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-card bg-surface shadow-[inset_0_0_0_1px_var(--hair)]">
      <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 border-b border-[var(--hair)] px-3 py-2 text-[11px] text-ink-3">
        <span>Источник</span>
        <span className="text-right">Старт</span>
        <span className="text-right">Биз.</span>
        <span className="text-right">С записью</span>
      </div>
      {props.rows.map((r) => (
        <div
          key={r.source}
          className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 px-3 py-2 text-[14px]"
        >
          <span className="truncate font-semibold">{r.source}</span>
          <span className="text-right tabular-nums">{r.started}</span>
          <span className="text-right tabular-nums">{r.businesses}</span>
          <span className="text-right tabular-nums">{r.withBooking}</span>
        </div>
      ))}
    </div>
  );
}

// ── Ссылки-метки ────────────────────────────────────────────────────────────

/** Часть после `src_`: строго как у `ACQUISITION_SOURCE` на бэкенде, нижний регистр. */
const LABEL_RE = /^[a-z0-9_]{1,40}$/;
/** Список созданных меток — на устройстве владельца: это памятка, а не источник трафика. */
const LABELS_KEY = 'app.adminSrcLabels';

function readLabels(): string[] {
  try {
    const arr = JSON.parse(localStorage.getItem(LABELS_KEY) ?? '[]');
    return Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function writeLabels(labels: string[]): void {
  try {
    localStorage.setItem(LABELS_KEY, JSON.stringify(labels));
  } catch {
    // Приватный режим или отключённое хранилище: список не запомнится, но ссылки работают —
    // метка живёт в самой ссылке (`src_…`), а не в этом списке.
  }
}

function linkFor(label: string): string {
  return `https://t.me/${BRAND.botUsername}?start=src_${label}`;
}

/**
 * Генератор ссылок-меток. Сам трекинг уже готов: `/start src_<метка>` пишет источник в
 * `users.acquisition_source`, а воронка выше — отчёт по нему. Здесь только сборка ссылки
 * из названия, которое владелец вводит сам, и памятка созданных меток (localStorage).
 * Счётчик переходов — из той же воронки (нажали «Старт»); нет строки — ещё ни одного.
 */
function LinkBuilder(props: { funnel: readonly AdminFunnelRowDto[] }): JSX.Element {
  const [label, setLabel] = useState('');
  const [labels, setLabels] = useState<string[]>(readLabels);
  const [copied, setCopied] = useState<string | null>(null);

  const clean = label.trim().toLowerCase();
  const valid = LABEL_RE.test(clean);
  const startedBy = new Map(props.funnel.map((r) => [r.source, r.started]));

  const add = (): void => {
    if (valid && !labels.includes(clean)) {
      const next = [clean, ...labels];
      setLabels(next);
      writeLabels(next);
    }
    setLabel('');
  };

  const remove = (l: string): void => {
    const next = labels.filter((x) => x !== l);
    setLabels(next);
    writeLabels(next);
  };

  const copy = async (l: string): Promise<void> => {
    try {
      await copyText(linkFor(l));
      setCopied(l);
    } catch {
      // Копирование недоступно — ссылка показана и выделяется вручную (`select-all`).
    }
  };

  return (
    <div className="rounded-card bg-surface p-3 shadow-[inset_0_0_0_1px_var(--hair)]">
      <div className="flex gap-2">
        <span className="flex items-center rounded-[10px] bg-surface-2 px-2 text-[13px] text-ink-3">
          src_
        </span>
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') add();
          }}
          placeholder="instagram"
          className="min-w-0 flex-1 rounded-[10px] bg-surface-2 px-3 py-2 text-[14px] text-ink outline-none placeholder:text-ink-3"
        />
        <SmallButton variant="accent" onClick={add}>
          Добавить
        </SmallButton>
      </div>
      <div className="mt-1.5 text-[11.5px] text-ink-3">
        {label && !valid
          ? 'Только латиница, цифры и _ (до 40 символов)'
          : 'Название в конце ссылки. Поставишь её в Instagram — переходы попадут сюда.'}
      </div>

      {labels.length > 0 ? (
        <div className="mt-3 flex flex-col gap-2 border-t border-hair pt-3">
          {labels.map((l) => (
            <div key={l} className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-semibold">src_{l}</div>
                <div className="select-all break-all text-[11px] text-ink-3">{linkFor(l)}</div>
              </div>
              <span className="shrink-0 text-[11.5px] text-ink-2">
                переходов: {startedBy.get(`src_${l}`) ?? 0}
              </span>
              <SmallButton onClick={() => copy(l)}>
                {copied === l ? 'Скопировано' : 'Копировать'}
              </SmallButton>
              <button
                type="button"
                onClick={() => remove(l)}
                className="shrink-0 px-1 text-[16px] leading-none text-ink-3 active:opacity-60"
                aria-label="Убрать из списка"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

const PLAN_LABEL: Record<AdminBusinessRowDto['plan'], string> = {
  pro: 'Pro',
  trial: 'Триал',
  free: 'Free',
};

function Businesses(props: {
  rows: readonly AdminBusinessRowDto[];
  onOpen: (businessId: string) => void;
}): JSX.Element {
  if (props.rows.length === 0) {
    return (
      <div className="rounded-card bg-surface px-3 py-3 text-[13px] text-ink-3 shadow-[inset_0_0_0_1px_var(--hair)]">
        Организаций ещё нет.
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      {props.rows.map((b) => (
        <button
          key={b.id}
          type="button"
          onClick={() => props.onOpen(b.id)}
          className="rounded-card bg-surface px-3 py-3 text-left shadow-[inset_0_0_0_1px_var(--hair)] active:opacity-70"
        >
          <div className="flex items-center justify-between gap-3">
            <span className="min-w-0 truncate font-semibold">{b.name}</span>
            <span className="shrink-0 text-[11.5px] text-ink-3">
              {PLAN_LABEL[b.plan]}
              {b.isPublished ? '' : ' · черновик'}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-[12.5px] text-ink-2">
            {b.city ? <span>{b.city}</span> : null}
            <span>клиентов: {b.clients}</span>
            <span>записей: {b.appointments}</span>
            <span>неявок: {b.noShow}</span>
            <span>
              активность:{' '}
              {b.lastActivityAt ? new Date(b.lastActivityAt).toLocaleDateString('ru') : '—'}
            </span>
          </div>
        </button>
      ))}
    </div>
  );
}
